/**
 * Step-progress affordance — a subtle "thread" of the referee's four working steps
 * (Details → Record → Review → Sign). Echoes the recording-screen prompt dots from the mock:
 * completed nodes fill sage, the current node is an elongated coral pill, upcoming nodes are muted.
 * Hidden on Landing / Done / terminal pages (the shell decides when to render it).
 */
import type { FlowStep } from '@/session/steps';
import { PROGRESS_NODES, progressActiveIndex } from '@/session/progress';
import { cn } from '@/lib/utils';

export function StepProgress({ step }: { step: FlowStep }) {
  const active = progressActiveIndex(step);
  if (active < 0) return null;
  const current = PROGRESS_NODES[active];

  return (
    <div className="flex flex-col items-center gap-2" aria-hidden="true">
      <div className="flex items-center gap-1.5">
        {PROGRESS_NODES.map((node, i) => {
          const done = i < active;
          const isCurrent = i === active;
          return (
            <span
              key={node.key}
              className={cn(
                'h-[5px] rounded-pill transition-all duration-ui ease-thread',
                isCurrent ? 'w-4 bg-coral' : 'w-[5px]',
                done && 'bg-sage',
                !done && !isCurrent && 'bg-line-2',
              )}
            />
          );
        })}
      </div>
      <span className="font-display text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-quiet">
        Step {active + 1} of {PROGRESS_NODES.length} · {current.label}
      </span>
    </div>
  );
}
